import { useEffect, useState, type ComponentType, type ReactNode } from 'react'

import { NotYourJob } from '../components/NotYourJob'
import { onScreen, screensPersonaName } from '../lib/screens'
import { ApprovalMatrix } from './ApprovalMatrix'
import { CustomFields } from './CustomFields'
import { OnboardingProcess } from './OnboardingProcess'
import { Users } from './Users'
import { EmailDelivery } from './settings/EmailDelivery'
import { IdentitySso } from './settings/IdentitySso'
import { Integrations } from './settings/Integrations'
import { PlatformPairing } from './settings/PlatformPairing'
import { VendorMessaging } from './settings/VendorMessaging'
import { RiskRegisters } from './settings/RiskRegisters'
import { VendorRegistry } from './settings/VendorRegistry'
import { OrganisationTab } from './settings/OrganisationTab'
import { ReassessmentDefaults } from './settings/Reassessment'
import { BaselinesSettings } from './settings/Baselines'
import { VendorGroupsSettings } from './settings/VendorGroups'
import { Workflows } from './settings/Workflows'
import { SuiteLinksSettings } from './settings/SuiteLinks'
import { DueDiligenceSettings } from './settings/DueDiligence'
import { ExceptionAuthoritySettings } from './settings/ExceptionAuthority'
import { SettingsFrame } from './settings/SettingsFrame'
import { SettingsIndex } from './settings/SettingsIndex'
import { currentEntry, entryScreen, shownEntries, type Entry } from './settings/entries'

// Settings, arranged the way FOCTTA's consent product arranges them.
//
// # WHY THIS IS A LIST AND NOT THE CARD GRID IT WAS
//
// The grid of eighteen cards treated every setting as equally important and
// left a person to read all of them to find the one they wanted. The consent
// product settled this differently and settled it well: five named groups and
// each setting a page in its own right. The index #/settings lands on (below)
// is not the grid again: one line each, saying what the setting decides, and a
// search, so somebody who has come to configure mail finds it in one step.
//
// A customer running both products should find the same shape in both, so the
// groups, the order and the vocabulary here are the consent product's. Where
// this product has no equivalent of one of its entries, the entry is absent
// rather than empty.
//
// # WHERE #/settings LANDS (13 Oct 2026, plan 3.9)
//
// On the index of every setting this reader holds, one line each, with a
// search (settings/SettingsIndex.tsx). Each entry opens in the frame below with
// the breadcrumb "Settings / <entry>", whose first crumb is the way back to the
// index. Until then #/settings opened the first entry held.
//
// # WHY EVERY ENTRY OPENS IN THE SAME PLACE
//
// Six of the entries are screens with addresses of their own, and pressing one
// replaced the page, Settings and its list with it. The founder's words on
// seeing it: the navigation inside settings is confusing, and a button on top
// leaves no way back to main settings. The mix of entries that switch a pane
// and entries that leave was the confusion, so this route draws every entry,
// pane or screen, inside one frame (settings/SettingsFrame.tsx). The addresses
// are unchanged: #/approval-matrix and its five are still the routes App.tsx
// sends here, with the screen they name.
//
// # WHY EACH PANE IS ITS OWN FILE
//
// Email delivery alone is a provider picker, a preset that fills three fields,
// credentials, a save with a dirty state and a separate test that sends a real
// message. That is a screen. Four of those in one file is the four-thousand
// line settings page the consent product now has to live with, and there is no
// reason to reproduce that part.
//
// # WHAT IS NOT HERE, AND WHY
//
// This page held twenty-one tabs, and eleven of them were links to screens the
// side navigation already lists: the questionnaire library, the scoring models,
// the risk matrix, the reassessment schedule, the two risk bridges, bringing
// vendors in, the audit trail, the evidence chain and the programme overview.
// Every one of them lives under Vendors, Assessments, Risk or Reports, which is
// where somebody looks for them. Repeating them here made settings the longest
// page in the product and made a reader wonder which of the two copies was the
// real one.
//
// What is left is what settings actually means: a decision made once that
// everything else then follows. Who approves, who may sign in, what a person
// may do, how mail leaves, what this product is connected to.

// The address is read from the window at each render, not kept in state: App
// draws this again on every change of address, and a copy held here would be
// the old address for that first draw, which showed the first entry for a moment
// before the one asked for. The listener is only so that a change that leaves
// App's route as it was (#/settings?t=email to #/settings?t=sso) draws again.
function useHash(): string {
  const [, again] = useState(0)
  useEffect(() => {
    const on = () => again((n) => n + 1)
    window.addEventListener('hashchange', on)
    return () => window.removeEventListener('hashchange', on)
  }, [])
  return window.location.hash
}

// What an entry draws. A pane carries the id the persona walk reads and a
// heading of its own: its panels have only h2s, and the page needs its one h1.
function pane(e: Entry, body: ReactNode): ReactNode {
  return (
    <div className="settings-pane" data-page={'tprm.settings-' + e.key}>
      <h1 className="vh">{e.label}</h1>
      {body}
    </div>
  )
}

// What each entry draws, by its key, in one table so that a test can hold it
// against what the entry is. The screens of their own keep their own heading and
// their own data-page (main carries it, from the route name); the panes are
// wrapped by pane().
export const VIEWS: Record<string, ComponentType> = {
  organisation: OrganisationTab,
  reassessment: ReassessmentDefaults,
  baselines: BaselinesSettings,
  'vendor-groups': VendorGroupsSettings,
  workflows: Workflows,
  sso: IdentitySso,
  email: EmailDelivery,
  messaging: VendorMessaging,
  integrations: Integrations,
  registers: RiskRegisters,
  'vendor-registry': VendorRegistry,
  platform: PlatformPairing,
  'suite-links': SuiteLinksSettings,
  onboarding: OnboardingProcess,
  approvals: ApprovalMatrix,
  'due-diligence': DueDiligenceSettings,
  'exception-authority': ExceptionAuthoritySettings,
  'custom-fields': CustomFields,
  people: Users,
}

function contentOf(e: Entry): ReactNode {
  const View = VIEWS[e.key]
  if (!View) return null
  return e.screen ? <View /> : pane(e, <View />)
}

// Settings, and each of its six screens, as App.tsx routes them: `screen` is the
// route name. On #/settings itself the open entry is the pane the address names;
// with none named, the index of the entries this reader holds.
export function SettingsRoute({ screen }: { screen: string }) {
  const hash = useHash()
  const shown = shownEntries()
  const current = currentEntry(screen, hash)

  // An entry asked for by address that is somebody else's job: the Integrations
  // pane opened by a DPO. It says whose job it is instead of the entry.
  if (current !== undefined && !onScreen(entryScreen(current))) {
    return <NotYourJob persona={screensPersonaName()} home="#/home" screen={entryScreen(current)} />
  }
  if (current === undefined && shown.length > 0) return <SettingsIndex shown={shown} />
  if (current === undefined) {
    return (
      <div className="p1-page">
        <div className="p1-head">
          <div>
            <h1>Settings</h1>
          </div>
        </div>
        <p className="muted" role="note">
          None of the settings of this workspace are part of your job.
        </p>
      </div>
    )
  }
  return (
    <SettingsFrame shown={shown} current={current}>
      <div key={current.key} className="settings-entry">
        {contentOf(current)}
      </div>
    </SettingsFrame>
  )
}
